import classnames from 'classnames'
import { Formik } from 'formik'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import Button from '../../components/basics/Button/Button'
import ClearTextInput from '../../components/basics/TextInput/ClearTextInput'
import TextInput, { TextInputFormik } from '../../components/basics/TextInput/TextInput'
import ThreadsDisplay from '../../components/comments/ThreadsDisplay/ThreadsDisplay'
import GroupUserOperations from '../../components/GroupUserOperations/GroupUserOperations'
import OverlayChildPage from '../../components/OverlayChildPage/OverlayChildPage'
import Pagination from '../../components/Pagination/Pagination'
import UserAvatar from '../../components/UserAvatar/UserAvatar'
import {
  userCanAddSomeUserToGroupAsGroupManager,
  userCanAddSomeUserToGroupAsMember,
  userCanCommentOnRoot,
  userCanEditGroup,
  userCanViewLearningAssignmentsForGroup,
  userCanViewPrivateGroupInformation,
} from '../../state/calculations/permissions/userCan'
import { useGroup } from '../../state/entities'
import useGroupUsersFilter from '../../state/queries/useGroupUsersFilter'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import useUpdateGroup from '../../state/mutations/useUpdateGroup'

import ManageGroupPageLearningAssignments from './ManageGroupPageLearningAssignments'
import styles from './ManageGroupPage.module.scss'
import ManageGroupPageLearningPathAssignments from "./ManageGroupPageLearningPathAssignments";

/**********************************************************************
 * ManageGroupPageGroupManagers
 **********************************************************************/
const ManageGroupPageGroupManagers = React.memo(function ManageGroupPageGroupManagers({ uuid }: { uuid: string }) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { user: currentUser } = useSession()

  const { group } = useGroup(uuid)

  const userFilter = useGroupUsersFilter(uuid, { prefix: 'managers', showMembers: false, showManagers: true })

  const updateSearch = useDebouncedCallback(userFilter.setSearch, 300)
  const [search, setSearch] = useState(userFilter.search)
  const handleSearchChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value)
      updateSearch(e.currentTarget.value)
    },
    [setSearch, updateSearch]
  )

  return (
    <section className={styles.ManageGroupPageGroupManagers}>
      <header>
        <h1>{t('group.group managers')}</h1>
      </header>

      <div className={styles.searchbar}>
        <TextInput
          name="search"
          label={t('search users')}
          type="text"
          autoComplete="off"
          omitErrorDisplay
          value={search || ''}
          onChange={handleSearchChanged}
          after={<ClearTextInput />}
        />
        {userCanAddSomeUserToGroupAsGroupManager(currentUser, group) && (
          <Button small component={Link} to={routes.addGroupManagersToGroups.url({ uuid })}>
            {t('manage group.add group managers')}
          </Button>
        )}
      </div>

      <div className={classnames(styles.usertable, { skeleton: userFilter.result.isPlaceholderData })}>
        <table>
          <tbody>
            {userFilter.users.map((user) => (
              <tr key={user.uuid} className={classnames({ [styles.inactive]: user.status !== 'active' })}>
                <td>
                  <GroupUserOperations groupUuid={uuid} userUuid={user.uuid} />
                </td>
                <td>
                  <UserAvatar link user={user as any} />
                </td>
                <td className={styles.nameColumn}>
                  <Link to={routes.manageUser.url({ uuid: user.uuid })}>
                    {user.firstname} {user.lastname}
                  </Link>
                </td>
                <td className={styles.infoColumn}>
                  <span>{user.isMember && t('group.user is also group member')}</span>
                </td>
                <td className={styles.infoColumn}>
                  <span>{t('user.user joined at date', { date: user.createdAt })}</span>
                  <span>{t(`user.user_status.${user.status}`)}</span>
                </td>
              </tr>
            ))}
            {!userFilter.users.length && (
              <tr>
                <td colSpan={5} className={styles.empty}>
                  {t('no group managers found')}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <Pagination
          page={userFilter.page}
          pageCount={userFilter.pageCount}
          pageSize={userFilter.pageSize}
          itemCount={userFilter.itemCount}
          onPageChanged={userFilter.setPage}
        />
      </div>
    </section>
  )
})

/**********************************************************************
 * ManageGroupPageMembers
 **********************************************************************/
const ManageGroupPageMembers = React.memo(function ManageGroupPageMembers({ uuid }: { uuid: string }) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { user: currentUser } = useSession()

  const { group } = useGroup(uuid)

  const userFilter = useGroupUsersFilter(uuid, { prefix: 'members', showMembers: true, showManagers: false })

  const updateSearch = useDebouncedCallback(userFilter.setSearch, 300)
  const [search, setSearch] = useState(userFilter.search)
  const handleSearchChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value)
      updateSearch(e.currentTarget.value)
    },
    [setSearch, updateSearch]
  )

  return (
    <section className={styles.ManageGroupPageMembers}>
      <header>
        <h1>{t('group.group members')}</h1>
      </header>

      <div className={styles.searchbar}>
        <TextInput
          name="search"
          label={t('search users')}
          type="text"
          autoComplete="off"
          omitErrorDisplay
          value={search || ''}
          onChange={handleSearchChanged}
        />
        {userCanAddSomeUserToGroupAsMember(currentUser, group) && (
          <Button small component={Link} to={routes.addMembersToGroups.url({ uuid })}>
            {t('manage group.add members')}
          </Button>
        )}
      </div>

      <div className={classnames(styles.usertable, { skeleton: userFilter.result.isPlaceholderData })}>
        <table>
          <tbody>
            {userFilter.users.map((user) => (
              <tr key={user.uuid} className={classnames({ [styles.inactive]: user.status !== 'active' })}>
                <td>
                  <GroupUserOperations groupUuid={uuid} userUuid={user.uuid} />
                </td>
                <td>
                  <UserAvatar link user={user as any} />
                </td>
                <td className={styles.nameColumn}>
                  <Link to={routes.manageUser.url({ uuid: user.uuid })}>
                    {user.firstname} {user.lastname}
                  </Link>
                </td>
                <td className={styles.infoColumn}>
                  <span>{user.isGroupManager && t('group.user is also group manager')}</span>
                </td>
                <td className={styles.infoColumn}>
                  <span>{t('user.user joined at date', { date: user.createdAt })}</span>
                  <span>{t(`user.user_status.${user.status}`)}</span>
                </td>
              </tr>
            ))}
            {!userFilter.users.length && (
              <tr>
                <td colSpan={5} className={styles.empty}>
                  {t('no members found')}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <Pagination
          page={userFilter.page}
          pageCount={userFilter.pageCount}
          pageSize={userFilter.pageSize}
          itemCount={userFilter.itemCount}
          onPageChanged={userFilter.setPage}
        />
      </div>
    </section>
  )
})

/**********************************************************************
 * ManageGroupPage
 **********************************************************************/
export default React.memo(function ManageGroupPage() {
  const { t } = useTranslation()
  const { user: currentUser } = useSession()

  const updateGroup = useUpdateGroup()

  const { uuid } = useParams()
  if (!uuid) {
    throw new Error('Group not found')
  }

  const { group } = useGroup(uuid)
  if (group.error) {
    throw new Error('Group not found')
  }

  const [isEditingName, setIsEditingName] = useState(false)

  const canEdit = userCanEditGroup(currentUser, group)

  return (
    <article className={classnames(styles.ManageGroupPage, { skeleton: group.isDummy })}>
      <div className={styles.detailsSection}>
        <header>
          <hgroup>
            {!isEditingName && (
              <>
                <h1>{group.name}</h1>
                {canEdit && (
                  <Button small onClick={() => setIsEditingName(true)}>
                    {t('generic cta.edit')}
                  </Button>
                )}
              </>
            )}
            {isEditingName && (
              <Formik
                enableReinitialize={true}
                initialValues={{ uuid: group.uuid, name: group.name }}
                onSubmit={(values) => updateGroup.mutate(values, { onSuccess: () => setIsEditingName(false) })}
              >
                {(props) => (
                  <form noValidate onSubmit={props.handleSubmit}>
                    <TextInputFormik label={t('name')} name="name" required className={styles.editNameInput} />
                    <Button small filled type="submit">
                      {t('generic cta.save')}
                    </Button>
                    <Button small onClick={() => setIsEditingName(false)}>
                      {t('generic cta.cancel')}
                    </Button>
                  </form>
                )}
              </Formik>
            )}
          </hgroup>
        </header>
      </div>

      <ManageGroupPageGroupManagers uuid={uuid} />

      {userCanViewPrivateGroupInformation(currentUser, group) && <ManageGroupPageMembers uuid={uuid} />}

      {userCanCommentOnRoot(currentUser, group) && <ThreadsDisplay groupUuid={uuid} root={group} />}

      {userCanViewLearningAssignmentsForGroup(currentUser, group) && <ManageGroupPageLearningPathAssignments uuid={uuid} />}

      {userCanViewLearningAssignmentsForGroup(currentUser, group) && <ManageGroupPageLearningAssignments uuid={uuid} />}

      <OverlayChildPage />
    </article>
  )
})

export function ManageGroupPageBreadcrumb() {
  const { uuid } = useParams()
  const { group } = useGroup(uuid!)
  const routes = useRoutes()
  return <Link to={routes.manageGroup.url({ uuid: uuid! })}>{group?.name}</Link>
}
